import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import useTable from 'shared/hooks/customHooks/useTable';
import { useAccounts } from 'users/new-user-management/hooks/reactQuery/useAccounts';
import { useCostCenters } from 'users/new-user-management/hooks/reactQuery/useCostCenters';
import { CostCentersContext } from './contexts/costCentersContext';
import CostCentersHeader from './CostCentersHeader';
import CostCentersTable from './CostCentersTable';
import CostCentersBanner from './CostCentersBanner';
import { findFilteredData } from 'users/containers/Organization/CostCenters/costCentersHelperFunctions.js';
import { useOrgToCustomers } from 'users/containers/Organization/components/OrgToCustomersSwitch/useOrgToCustomers';
import { COST_CENTERS_VIEWS } from 'users/containers/Organization/consts';

import styles from './CostCenters.module.scss';

const CostCenters = ({
  isCreateEditMode,
  isAddRemoveCostCentersHidden,
  isRoleCostCentersDataAccessLoading,
  isRoleCostCentersDataAccessView,
  isSelectionEnabled = true,
  isRoleEditable,
  onSelectionChange,
  roleCostCentersDataAccess,
  roleId,
}) => {
  const useCostCentersAndUseAccountsCombined = (isDisabled) => ({
    ...useCostCenters(isDisabled),
    ...useAccounts(isDisabled),
  });
  const {
    selectedCustomerDetails,
    handleOrgCustomersToggle,
    handleCustomerSelectionChanged,
    isCustomersView,
    setIsCustomersView,
    hookData,
    isCustomerTokensLoading,
  } = useOrgToCustomers(useCostCentersAndUseAccountsCombined, initializeState);

  const [search, setSearch] = useState();
  const [costCentersView, setCostCentersView] = useState(COST_CENTERS_VIEWS.TREE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllExpanded, setIsAllExpanded] = useState(false);

  const { NewTableWrapper } = useTable();

  const { data: costCentersData, isLoading: isCostCentersDataAccessLoading } = hookData.fetchManagementCostCenters(
    isCustomersView,
    selectedCustomerDetails?.customer,
  );

  const { data: payerAccountsData } = hookData.fetchDataAccessPayerAccounts(
    isCustomersView,
    selectedCustomerDetails?.customer,
  );

  const { fetchCostCenterUnassignedAccounts } = useCostCenters(
    isRoleCostCentersDataAccessView || isCustomerTokensLoading,
  );

  const { data: unassignedAccountsData, isLoading: isUnassignedAccountsDataLoading } =
    fetchCostCenterUnassignedAccounts({
      isCustomersView,
      selectedCustomer: selectedCustomerDetails?.customer,
    });

  const unassignedAccountsToDisplay = useMemo(() => {
    if (!unassignedAccountsData) {
      return null;
    }
    const unassignedAccountsToDisplay = [];
    if (unassignedAccountsData?.length && payerAccountsData?.length) {
      unassignedAccountsData.forEach((account) => {
        const payerAccount = payerAccountsData?.find(
          (payerAccount) => payerAccount.payerAccount.id === account.payerWLinked?.payerAccountId,
        );
        if (payerAccount) {
          account.payerWLinked?.linkedAccountIds?.forEach((linkedAccountId) => {
            const linkedAccountSearch = payerAccount.linkedAccounts?.find((la) => la.id === linkedAccountId);
            if (linkedAccountSearch) {
              unassignedAccountsToDisplay.push({
                payerAccountId: payerAccount.payerAccount.id,
                payerAccountName: payerAccount.payerAccount.name,
                linkedAccountId: linkedAccountSearch.id,
                linkedAccountName: linkedAccountSearch.name,
                cloudType: payerAccount.payerAccount?.cloudType,
              });
            }
          });
        }
      });
    }
    return { id: 'unassigned', name: 'Unassigned', parentId: null, unassignedAccounts: unassignedAccountsToDisplay };
  }, [payerAccountsData, unassignedAccountsData]);

  const costCentersToDisplay = useMemo(() => {
    let data = isRoleCostCentersDataAccessView ? roleCostCentersDataAccess : costCentersData;

    let result = null;
    if (data) {
      result = data.map((cc) => {
        const transformedCostCenter = {
          ...{ ...cc?.costCenter, parentId: cc?.costCenter?.parentCcId },
          accounts: cc.accounts,
          creationTime: cc.creationTime,
        };

        delete transformedCostCenter.parentCcId;

        return transformedCostCenter;
      });
      if (search) {
        result = findFilteredData(result, search, null);
      }
    }
    if (
      unassignedAccountsToDisplay &&
      !isRoleCostCentersDataAccessView &&
      !(isCustomersView && !selectedCustomerDetails?.customer)
    ) {
      result = [unassignedAccountsToDisplay, ...(Array.isArray(result) ? result : [])];
    }
    return result;
  }, [
    costCentersData,
    isCustomersView,
    isRoleCostCentersDataAccessView,
    roleCostCentersDataAccess,
    search,
    selectedCustomerDetails?.customer,
    unassignedAccountsToDisplay,
  ]);

  const totalCount = useMemo(() => {
    if (isRoleCostCentersDataAccessView) {
      if (isSelectionEnabled) {
        return costCentersToDisplay?.filter((cc) => cc.isAllowed === true).length;
      }
      return roleCostCentersDataAccess?.length || 0;
    }

    return costCentersData?.length;
  }, [
    costCentersData?.length,
    costCentersToDisplay,
    isRoleCostCentersDataAccessView,
    isSelectionEnabled,
    roleCostCentersDataAccess?.length,
  ]);

  function initializeState() {
    setSelectedRows([]);
    setIsAllExpanded(false);
  }

  return (
    <div className={classnames(styles.costCenters, { [styles.withPadding]: !isRoleCostCentersDataAccessView })}>
      {isRoleCostCentersDataAccessView ? null : <CostCentersBanner />}
      <CostCentersContext.Provider
        value={{
          costCentersView,
          setCostCentersView,
          isAllExpanded,
          isCustomersView,
          isCustomerTokensLoading,
          search,
          selectedCustomer: selectedCustomerDetails.customer,
          selectedRows,
          setIsAllExpanded,
          setIsCustomersView,
          setSearch,
          setSelectedRows,
        }}
      >
        <NewTableWrapper>
          <CostCentersHeader
            isAddRemoveCostCentersHidden={isAddRemoveCostCentersHidden}
            isRoleDataAccessView={isRoleCostCentersDataAccessView}
            isSelectionEnabled={isSelectionEnabled}
            isRoleEditable={isRoleEditable}
            onCustomerSelectionChanged={handleCustomerSelectionChanged}
            onOrgCustomersToggle={handleOrgCustomersToggle}
            roleId={roleId}
            total={totalCount}
          />
          <CostCentersTable
            accountsData={payerAccountsData || []}
            isCreateEditMode={isCreateEditMode ?? false}
            costCenters={costCentersToDisplay || []}
            isLoading={
              isCostCentersDataAccessLoading ||
              isRoleCostCentersDataAccessLoading ||
              isUnassignedAccountsDataLoading ||
              isCustomerTokensLoading
            }
            isSelectionEnabled={isSelectionEnabled}
            isRoleDataAccessView={isRoleCostCentersDataAccessView}
            onSelectionChange={onSelectionChange}
            noCostCentersInOrg={!isCostCentersDataAccessLoading && !costCentersData?.length}
          />
        </NewTableWrapper>
      </CostCentersContext.Provider>
    </div>
  );
};

export default CostCenters;
